/*
  PLUSH + OAK — CART RATE
  --------------------------------------------------------------------------
  The cart and the drawer both carried "Spend $7,321.00 more and get free
  shipping!" — the same deficit message that was removed from the product page,
  on the last screen before checkout, against a threshold four web orders a
  year reach. It told the buyer they had failed, then sent them into checkout
  with no idea what delivery costs.

  This replaces it with their actual rate. The figure is never hardcoded and
  never inferred: it comes from /cart/shipping_rates.json, which is Shopify
  pricing the real cart against a real destination through the same delivery
  profiles checkout will use. So Alaska, Hawaii and Canada are right by
  construction rather than by us guessing.

  The destination is whatever the shopper already answered on the product page
  (po-freight-note.js stores it). Nothing is asked twice, and if they never
  answered, no number is claimed at all.

  ⚠ That endpoint is not read-only. Querying it for an address with no
  available rate makes Shopify drop the unshippable items — the cart empties.
  It is only ever called here with a destination that already produced a quote,
  which is why po-freight-note.js stores a destination only on success.
*/
(function () {
  'use strict';

  var STORE_KEY = 'po-destination';

  var NO_DEST = 'Flat-rate delivery — one fee covers your whole order, however many pieces.';

  var ALLIN_CLASS = 'po-allin';
  var STYLE_ID = 'po-allin-style';
  var CSS = '.po-allin{display:block;margin:6px 0 0;font-size:.9em;opacity:.75;}';

  var cache = {};

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = CSS;
    document.head.appendChild(el);
  }

  function destination() {
    try {
      var raw = localStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var d = JSON.parse(raw);
      return (d && d.country && d.province && d.zip) ? d : null;
    } catch (e) {
      return null;
    }
  }

  function money(value) {
    var n = parseFloat(value);
    if (isNaN(n)) return null;
    /* Whole dollars read as a price; cents only appear when they are real. */
    var body = (n % 1 === 0) ? String(n) : n.toFixed(2);
    return '$' + body.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* Shopify returns every eligible service. Where several apply the cheapest is
     quoted, and the copy says so rather than implying it is the only price. */
  function cheapest(rates) {
    var best = null;
    for (var i = 0; i < rates.length; i++) {
      var price = parseFloat(rates[i].price);
      if (isNaN(price)) continue;
      if (!best || price < best.price) best = { price: price, name: rates[i].name };
    }
    return best;
  }

  function sentence(best, count, dest) {
    /* No rate at all is a real answer, not a missing one — some lanes are
       quoted by hand. Saying "flat-rate delivery" here would promise a price
       that does not exist for them. */
    if (!best) {
      return 'Delivery to ' + dest.province + ' is quoted individually — ' +
             'contact us and we will price your freight.';
    }

    var where = dest.province;
    if (best.price === 0) {
      return 'Your order ships free to ' + where + ' — fully insured, however many pieces.';
    }

    var amount = money(best.price);
    var unlimited = /unlimited/i.test(best.name);
    var canada = /canada/i.test(best.name);

    if (unlimited && canada) {
      return amount + ' Unlimited Delivery to ' + where +
             ' — the same flat rate to every province. Taxes shown at checkout.';
    }
    if (unlimited) {
      return amount + ' Unlimited Delivery to ' + where +
             '. All inclusive of insurance, duties, and fees. No surprises.';
    }
    /* A banded lane can return several services, and "From" is honest there.
       A single rate is the price, so it must not be hedged. */
    if (count > 1) {
      return 'From ' + amount + ' to ' + where + ' — choose your service at checkout.';
    }
    return amount + ' delivery to ' + where + ' — fully insured, door to door.';
  }

  /* The cart showed the merchandise total and the button repeated it, so the
     freight only appeared at checkout — the single most common reason a cart is
     abandoned. This states the all-in figure before they click.

     US orders carry no tax and no duty, so the number is exact and can be
     called final. Canada adds GST/PST/HST at checkout, so it is labelled as
     before tax rather than pretending to be the total. */
  function paintAllIn(text) {
    var anchors = [];
    var rows = document.querySelectorAll('.cart-recap .h-stack, cart-drawer .h-stack');
    for (var i = 0; i < rows.length; i++) {
      if (/Total:?\s*[$€£]/.test((rows[i].innerText || '').replace(/\s+/g, ' '))) anchors.push(rows[i]);
    }
    if (!anchors.length) anchors = Array.prototype.slice.call(document.querySelectorAll('free-shipping-bar'));

    for (var j = 0; j < anchors.length; j++) {
      var anchor = anchors[j];
      var line = anchor.nextElementSibling;
      if (!line || line.className !== ALLIN_CLASS) {
        if (!text) continue;
        line = document.createElement('span');
        line.className = ALLIN_CLASS;
        anchor.parentNode.insertBefore(line, anchor.nextSibling);
      }
      if (!text) { if (line.parentNode) line.parentNode.removeChild(line); continue; }
      if (line.textContent !== text) line.textContent = text;
    }
  }

  function allInSentence(best, cartCents, count) {
    if (!best || cartCents === null) return '';
    if (best.price === 0) {
      return 'Delivery included — ' + money(cartCents / 100) + ' all inclusive at checkout.';
    }
    var total = money(cartCents / 100 + best.price);

    /* Where several services apply the total depends on which they pick, so it
       is quoted as a floor rather than as the price. */
    if (count > 1) {
      return '+ ' + money(best.price) + ' delivery — from ' + total + ' at checkout.';
    }
    /* Canada adds GST/PST/HST on top, so it is the one lane that cannot claim
       to be all inclusive. Everywhere else the figure is the whole of it. */
    var tail = /canada/i.test(best.name) ? ' before tax.' : ' all inclusive at checkout.';
    return '+ ' + money(best.price) + ' delivery — ' + total + tail;
  }

  function paint(text) {
    var bars = document.querySelectorAll('free-shipping-bar');
    for (var i = 0; i < bars.length; i++) {
      var span = bars[i].querySelector('span');
      if (!span) continue;
      if (span.textContent !== text) span.textContent = text;
    }
  }

  /* The theme keeps total-price on the element and updates it as the cart
     changes, which makes it a reliable key for "has anything moved". */
  function cartKey() {
    var bar = document.querySelector('free-shipping-bar');
    return bar ? (bar.getAttribute('total-price') || '') : '';
  }

  function refresh() {
    var bar = document.querySelector('free-shipping-bar');
    if (!bar) return;

    injectStyle();

    var dest = destination();
    if (!dest) { paint(NO_DEST); paintAllIn(''); return; }

    var key = cartKey() + '|' + dest.country + '|' + dest.province + '|' + dest.zip;
    if (cache.key === key) { paint(cache.text); paintAllIn(cache.allIn || ''); return; }

    /* Paint the safe line while the request is in flight, so the deficit
       message is never what the shopper is left looking at. */
    paint(cache.text || NO_DEST);

    var qs = new URLSearchParams();
    qs.set('shipping_address[country]', dest.country);
    qs.set('shipping_address[province]', dest.province);
    qs.set('shipping_address[zip]', dest.zip);

    Promise.all([
      fetch('/cart/shipping_rates.json?' + qs.toString()).then(function (r) { return r.ok ? r.json() : null; }),
      fetch('/cart.js').then(function (r) { return r.ok ? r.json() : null; })
    ]).then(function (both) {
      var rates = (both[0] && both[0].shipping_rates) || [];
      var cartCents = both[1] ? both[1].total_price : null;
      var best = cheapest(rates);
      var text = sentence(best, rates.length, dest);
      var allIn = allInSentence(best, cartCents, rates.length);
      cache = { key: key, text: text, allIn: allIn };
      paint(text);
      paintAllIn(allIn);
    }).catch(function () { paint(NO_DEST); paintAllIn(''); });
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; refresh(); });
  }

  function start() {
    refresh();
    try {
      new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
    /* The drawer is re-rendered wholesale on every cart change, so the bar we
       painted is routinely replaced by a fresh one carrying the old copy. */
    setInterval(refresh, 600);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();